import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sun, Moon, Menu, X, ChevronDown, ArrowRight, RadioTower, Clock, Building2, GraduationCap } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { allCourses } from '../data/courses';
import osopLogoLight from '../assets/osop logo light.png';
import osopLogoDark from '../assets/osop logo dark.png';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const [showCourses, setShowCourses] = useState(false);
  const [activeCategory, setActiveCategory] = useState('dac');



  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.navbar-container')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const navItems = [
    { name: 'Why OSOPCoding?', path: '/why-osop' },
    { name: 'Placement', path: '/placement' },
    { name: 'Hire From Us', path: '/hire' },
    { name: 'Contact', path: '/contact' },
  ];

  // Course categories - Free courses hidden from entire website
  const courseCategories = [
    { slug: 'dac', title: "OSOP's Certification Programs", icon: <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" /> },
    { slug: 'live', title: 'Live Courses', icon: <RadioTower className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" /> },
    { slug: 'self-paced', title: 'Self-Paced', icon: <Clock className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" /> },
    { slug: 'offline', title: 'Offline', icon: <Building2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" /> },
  ];

  const isActive = (path) => location.pathname === path;

  const rightPanelVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const courseItemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: { opacity: 1, x: 0 }
  };

  return (
    <>
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-[10002] transition-all duration-300 navbar-container ${
          isScrolled 
            ? 'surface-glass backdrop-blur-lg shadow-elevation' 
            : 'surface-primary'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-16 sm:h-18 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center focus:outline-none flex-shrink-0">
              <img 
                src={isDark ? osopLogoLight : osopLogoDark} 
                alt="OSOPCoding Logo" 
                className={`w-auto ${isDark ? 'max-h-10 sm:max-h-11 lg:max-h-12' : 'max-h-12 sm:max-h-14 lg:max-h-16'} object-contain transition-all duration-300 focus:outline-none`} 
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
              {/* Courses Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setShowCourses(true)} 
                onMouseLeave={() => setShowCourses(false)}
              >
                <button
                  className={`flex items-center font-medium transition-colors duration-200 focus:outline-none border-none text-sm xl:text-base ${
                    location.pathname.startsWith('/courses')
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  Courses <ChevronDown className="w-4 h-4 ml-1" />
                </button>
                
                {/* Invisible hover area that extends below the button */}
                <div className="absolute top-full left-0 right-0 h-3 bg-transparent"></div>
                
                <AnimatePresence>
                  {showCourses && (
                    <motion.div 
                      className="absolute top-full -ml-6 mt-3 w-max bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden z-[10003]"
                      initial={{ opacity: 0, y: 10, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.98 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <div className="flex">
                        {/* Left Panel - Categories */}
                        <div className="w-64 xl:w-72 border-r border-gray-200/50 dark:border-gray-700/50 p-3 xl:p-4 bg-gradient-to-br from-gray-50/50 to-white/50 dark:from-gray-800/50 dark:to-gray-900/50">
                          <div className="mb-3 xl:mb-4">
                            <h3 className="font-bold text-base xl:text-lg text-gray-800 dark:text-white mb-1">Explore Our Courses</h3>
                            <p className="text-xs text-gray-600 dark:text-gray-400">Choose from our learning paths</p>
                          </div>
                          <div className="space-y-1">
                            {courseCategories.map(cat => (
                              <Link
                                key={cat.slug} 
                                to={`/courses/${cat.slug}`}
                                onMouseEnter={() => setActiveCategory(cat.slug)}
                                className={`group w-full flex items-center text-left px-2 xl:px-3 py-2 xl:py-2.5 rounded-lg text-xs xl:text-sm font-medium transition-all duration-200 ${
                                  activeCategory === cat.slug 
                                    ? 'bg-primary-500 text-white shadow-md' 
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                                onClick={() => setShowCourses(false)}
                              >
                                <div className={`transition-colors duration-200 ${
                                  activeCategory === cat.slug ? 'text-white' : 'text-primary-600 dark:text-primary-400'
                                }`}>
                                  {cat.icon}
                                </div>
                                <span className="flex-1">{cat.title}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                        
                        {/* Right Panel - Course Preview */}
                        <motion.div 
                          className="w-[320px] xl:w-[380px] p-3 xl:p-4"
                          key={activeCategory}
                          variants={rightPanelVariants}
                          initial="hidden"
                          animate="visible"
                        >
                          <div className="mb-3 xl:mb-4">
                            <h4 className="font-bold text-sm xl:text-base text-gray-800 dark:text-white mb-1 capitalize">
                              {courseCategories.find(cat => cat.slug === activeCategory)?.title}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              {activeCategory === 'dac' && 'Professional diploma programs'}
                              {activeCategory === 'live' && 'Interactive live sessions'}
                              {activeCategory === 'self-paced' && 'Learn at your own pace'}
                              {activeCategory === 'offline' && 'In-person classroom learning'}
                            </p>
                          </div>
                          
                          <div className="space-y-1 xl:space-y-2">
                            {allCourses[activeCategory]?.slice(0, 4).map((course, index) => (
                              <motion.div 
                                key={course.id} 
                                variants={courseItemVariants}
                                transition={{ delay: index * 0.03 }}
                              >
                                <Link 
                                  to={`/course/${course.slug || course.id}`} 
                                  onClick={() => setShowCourses(false)} 
                                  className="group block p-2 xl:p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                                >
                                  <h5 className="font-semibold text-gray-800 dark:text-white mb-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors duration-200 text-xs xl:text-sm line-clamp-2">
                                    {course.title}
                                  </h5>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{course.level}</p>
                                </Link>
                              </motion.div>
                            ))}
                          </div>
                          
                          {/* View All Button */}
                          <motion.div variants={courseItemVariants} className="mt-3 xl:mt-4 pt-2 xl:pt-3 border-t border-gray-200/50 dark:border-gray-700/50">
                            <Link 
                              to={`/courses/${activeCategory}`} 
                              onClick={() => setShowCourses(false)} 
                              className="flex items-center text-primary-600 hover:text-primary-700 font-medium text-xs xl:text-sm py-1.5 xl:py-2 px-2 xl:px-3 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/40 transition-colors duration-200"
                            >
                              <span>View All {activeCategory.replace('-', ' ')} courses</span>
                              <ArrowRight className="w-3 h-3 xl:w-4 xl:h-4 ml-1 xl:ml-2" />
                            </Link>
                          </motion.div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Other Navigation Items */}
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`font-medium transition-colors duration-200 focus:outline-none border-none text-sm xl:text-base ${
                    isActive(item.path)
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Right Side - Theme Toggle, Franchise Button, Mobile Menu */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 focus:outline-none border-none flex-shrink-0"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <Sun className="w-4 h-4 text-yellow-400" />
                ) : (
                  <Moon className="w-4 h-4 text-gray-600" />
                )}
              </button>

              {/* Franchise Button - Better responsive visibility */}
              <Link
                to="/franchise"
                className="hidden sm:block bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-2 px-3 sm:px-4 rounded-full shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 focus:outline-none border-none text-xs sm:text-sm flex-shrink-0"
              >
                Franchise
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-2 focus:outline-none border-none rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 flex-shrink-0"
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                ) : (
                  <Menu className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu - Rendered outside navbar to avoid backdrop blur issues */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 top-16 sm:top-18 lg:top-20 bg-white dark:bg-gray-900 z-[10005] overflow-y-auto"
          style={{
            position: 'fixed',
            top: '4rem',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10005,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="flex flex-col min-h-full">
            <div className="flex-1 px-4 py-6 space-y-2">
              {/* Mobile Courses Dropdown */}
              <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileCoursesOpen(!mobileCoursesOpen);
                  }} 
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMobileCoursesOpen(!mobileCoursesOpen);
                  }}
                  className="flex justify-between items-center w-full text-left py-3 px-4 rounded-xl font-semibold text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 text-base"
                >
                  <span>Courses</span>
                  <ChevronDown className={`w-5 h-5 transition-transform duration-200 flex-shrink-0 ${mobileCoursesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {mobileCoursesOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-2 space-y-1 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-3 border border-gray-200/50 dark:border-gray-700/50"
                    >
                      {courseCategories.map(cat => (
                        <Link 
                          key={cat.slug} 
                          to={`/courses/${cat.slug}`} 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpen(false);
                            setMobileCoursesOpen(false);
                            setTimeout(() => {
                              window.location.href = `/courses/${cat.slug}`;
                            }, 100);
                          }} 
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsOpen(false);
                            setMobileCoursesOpen(false);
                            setTimeout(() => {
                              window.location.href = `/courses/${cat.slug}`;
                            }, 100);
                          }}
                          className="flex items-center py-3 px-4 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700/50 transition-colors duration-200 text-sm font-medium"
                        >
                          <div className="flex-shrink-0 text-primary-600 dark:text-primary-400">
                            {cat.icon}
                          </div>
                          <span className="ml-3 truncate">{cat.title}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Mobile Navigation Items */}
              <div className="space-y-2">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsOpen(false);
                      // Force navigation after a small delay
                      setTimeout(() => {
                        window.location.href = item.path;
                      }, 100);
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsOpen(false);
                      setTimeout(() => {
                        window.location.href = item.path;
                      }, 100);
                    }}
                    className={`block py-3 px-4 rounded-xl font-semibold transition-colors duration-200 text-base ${
                      isActive(item.path) 
                        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/40' 
                        : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Mobile Franchise Button - Fixed at bottom */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
              <Link
                to="/franchise"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Navigating to franchise');
                  setIsOpen(false);
                  setTimeout(() => {
                    window.location.href = '/franchise';
                  }, 100);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Touch navigation to franchise');
                  setIsOpen(false);
                  setTimeout(() => {
                    window.location.href = '/franchise';
                  }, 100);
                }}
                className="block w-full py-4 px-6 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white font-bold text-center text-base hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg"
              >
                🚀 Franchise Opportunities
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar; 